import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, zip } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Wrap Material snack bar component for different styles depending on message type (info / error).
 */
@Injectable({
  providedIn: 'root',
})
export class SnackBarService {
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  /**
   * Show message and keep it until it is manually dismissed by the user.
   *
   * @param messageKey key of the message to show
   * @param interpolateParams optional message params
   */
  public showError(
    messageKey: string | Array<string>,
    interpolateParams?: object,
  ) {
    // TODO show different color for info and error messages
    zip(
      this.translate.get(messageKey, interpolateParams),
      this.translate.get('GENERAL.CLOSE'),
    ).subscribe(([infoText, closeText]) =>
      this.snackbar.open(infoText, closeText),
    );
  }

  /**
   * Show message and keep it until it is manually dismissed by the user.
   *
   * @param messageKey key of the message to show
   * @param actionKey key of the message action to show
   * @param onAction function to execute when user clicked on the link in the message
   */
  public showErrorWithAction(
    messageKey: string | Array<string>,
    actionKey: string | Array<string> = 'GENERAL.CLOSE',
  ): Observable<void> {
    // TODO show different color for info and error messages
    const clicked = new Subject<void>();
    zip(
      this.translate.get(messageKey),
      this.translate.get(actionKey),
    ).subscribe(([infoText, actionText]) => {
      const snackBarRef = this.snackbar.open(infoText, actionText, {
        duration: 5000,
      });
      snackBarRef.onAction().subscribe(() => clicked.next());
    });
    return clicked.asObservable();
  }

  /**
   * Show message and automatically dismiss it after a fixed duration.
   *
   * @param messageKey key of the message to show
   * @param interpolateParams optional message params
   */
  public showMessage(
    messageKey: string | Array<string>,
    interpolateParams?: object,
  ) {
    this.translate
      .get(messageKey, interpolateParams)
      .subscribe((translatedText) =>
        this.snackbar.open(translatedText, undefined, { duration: 2000 }),
      );
  }
}
